import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useApp } from '../context/AppContext';
import api from '../api/api';
import { CheckCircle, Home as HomeIcon, MapPin, Pencil, Plus, Trash2, X } from 'lucide-react';
import { formatDate } from '../utils/helpers';
import { formatMealType, getCartRestrictionForAddress, MEAL_TYPES } from '../utils/rainAvailability';

const getAddressLineValue = (address) =>
  [address?.Address1 || address?.Address || address?.AddressLine, address?.Address2]
    .filter((part) => part && String(part).trim())
    .join(', ');

const getAddressCityValue = (address) => (address?.City || address?.Area || '').trim();
const getAddressDivisionValue = (address) => (address?.Division || address?.Route || '').trim();
const getAddressIdValue = (address) =>
  Number(address?.AId || address?.Aid || address?.AID || address?.aId || address?.AddressId || address?.Id || address?.id || 0);

const getAddressIdentityKey = (address) =>
  [
    address?.ShortName,
    getAddressLineValue(address),
  ]
    .map((part) => String(part || '').trim().toLowerCase())
    .join('|');

const getAddressDedupeKeys = (address) => {
  const keys = [];
  const addressId = getAddressIdValue(address);
  if (addressId) keys.push(`id:${addressId}`);
  keys.push(`identity:${getAddressIdentityKey(address)}`);
  return keys;
};

const dedupeAddresses = (addresses) => {
  const seen = new Set();
  return addresses.filter((address) => {
    const keys = getAddressDedupeKeys(address);
    if (keys.some((key) => seen.has(key))) return false;
    keys.forEach((key) => seen.add(key));
    return true;
  });
};

export default function Header() {
  const { user, cart, findMenuItem, logout, setUser, setCart } = useApp();
  const navigate = useNavigate();
  const location = useLocation();
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isAddressModalOpen, setIsAddressModalOpen] = useState(false);
  const [additionalAddresses, setAdditionalAddresses] = useState([]);
  const [deletingAddressId, setDeletingAddressId] = useState(null);
  const [addressRestrictionModal, setAddressRestrictionModal] = useState(null);
  const [helpPages, setHelpPages] = useState([]);
  const dropdownRef = useRef(null);
  const selectedAddress = user?.selectedAdditionalAddress;
  const primaryShortName = user?.ShortName?.trim?.() || 'Primary';
  const primaryAddress = {
    Vid: user?.Vid,
    ShortName: primaryShortName,
    Address1: user?.Address1 || (!selectedAddress ? user?.address : '') || '',
    Address2: user?.Address2 || (!selectedAddress ? user?.landmark : '') || '',
    City: user?.City || user?.city || (!selectedAddress ? user?.area : '') || '',
    Division: user?.Division || (!selectedAddress ? user?.division : '') || '',
    AreaId: user?.AreaId || user?.AreaID || user?.areaId,
    IsCodAvail: user?.IsCodAvail,
  };
  const primaryAddressKey = `primary-${user?.Vid || 'user'}`;
  const isPrimarySelected = !selectedAddress || selectedAddress?.selectedKey === primaryAddressKey || selectedAddress?.isPrimary;
  const useTamilLogo = ['/history', '/profile'].some((path) =>
    location.pathname.toLowerCase().startsWith(path)
  );
  const getAddressKey = (address, index, prefix = 'additional') =>
    `${prefix}-${address?.Vid || user?.Vid || 'user'}-${address?.ShortName || 'address'}-${index}`;

  useEffect(() => {
    fetchHelpPages();
  }, []);

  useEffect(() => {
    if (!isAddressModalOpen || !user?.Vid) return;

    const fetchAdditionalAddresses = async () => {
      try {
        const res = await api.GetMultipleAdd(user.Vid);
        const data = res?.data?.GetMultipleAddjs || res?.GetMultipleAddjs || {};
        const savedAddresses = Array.isArray(data?.Table1) ? data.Table1 : [];
        const sessionAddresses = JSON.parse(sessionStorage.getItem('cartAdditionalAddresses') || '[]');
        setAdditionalAddresses(dedupeAddresses([...savedAddresses, ...sessionAddresses]));
      } catch (err) {
        console.error('Failed to fetch additional addresses:', err);
      }
    };

    fetchAdditionalAddresses();
  }, [isAddressModalOpen, user?.Vid]);

  useEffect(() => {
    if (!isAddressModalOpen) return;

    const handleEscape = (event) => {
      if (event.key === 'Escape') {
        setIsAddressModalOpen(false);
      }
    };

    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [isAddressModalOpen]);

  useEffect(() => {
    const handleOpenAddressSelector = () => setIsAddressModalOpen(true);
    window.addEventListener('open-delivery-address-selector', handleOpenAddressSelector);
    return () => window.removeEventListener('open-delivery-address-selector', handleOpenAddressSelector);
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const fetchHelpPages = async () => {
    try {
      const response = await api.GetHelpPages();
      // Handle the API response structure: GetHelpPagesjs.Table
      const pages = response.GetHelpPagesjs?.Table || [];
      // Sort by PageSeqNo and filter by isvisible
      const sortedPages = pages
        .filter(page => page.isvisible === 1)
        .sort((a, b) => a.PageSeqNo - b.PageSeqNo);
      setHelpPages(sortedPages);
    } catch (error) {
      console.error('Error fetching help pages:', error);
    }
  };

  const handleHelpPageClick = (pageId) => {
    setIsDropdownOpen(false);
    navigate(`/help/${pageId}`);
  };

  const handleProfileClick = () => {
    setIsDropdownOpen(false);
    navigate('/profile');
  };

  const handleContactClick = () => {
    setIsDropdownOpen(false);
    navigate('/contact-annam');
  };

  const handleLogout = () => {
    setIsDropdownOpen(false);
    logout();
  };

  const toggleDropdown = () => {
    setIsDropdownOpen(!isDropdownOpen);
  };

  const validateAddressCartItems = async (address) => {
    const cartDates = Object.keys(cart || {});
    if (!cartDates.length) return { affectedItems: [], blockedMealTypes: [] };

    const restrictionRowsByDate = {};
    const restrictionContentRowsByDate = {};
    await Promise.all(
      cartDates.map(async (date) => {
        const res = await api.GetMenuplan(date);
        restrictionRowsByDate[date] = res?.GetMenuplanjs?.Table3 || [];
        restrictionContentRowsByDate[date] = res?.GetMenuplanjs?.Table4 || [];
      })
    );

    return getCartRestrictionForAddress({
      cart,
      findMenuItem,
      restrictionRowsByDate,
      restrictionContentRowsByDate,
      address,
    });
  };

  const blockAddressChangeWithCartItems = (address, restrictionCheck, updatedUser) => {
    setAddressRestrictionModal({
      addressName: address?.ShortName || 'selected address',
      addressArea: getAddressCityValue(address),
      affectedItems: restrictionCheck.affectedItems,
      blockedMealTypes: restrictionCheck.blockedMealTypes,
      wording: restrictionCheck.wording,
      updatedUser,
    });
    setIsAddressModalOpen(false);
  };

  const persistSelectedUser = (updatedUser) => {
    setUser(updatedUser);
    sessionStorage.setItem('user', JSON.stringify(updatedUser));
    localStorage.setItem('user', JSON.stringify(updatedUser));
    setIsAddressModalOpen(false);
  };

  const removeBlockedItemsAndUseAddress = () => {
    if (!addressRestrictionModal?.updatedUser) return;

    const blockedItemKeys = new Set(
      addressRestrictionModal.affectedItems.map((item) => `${item.date}|${item.itemId}`)
    );

    setCart((prev) => {
      const next = {};
      Object.entries(prev || {}).forEach(([date, itemsObj]) => {
        const keptItems = Object.fromEntries(
          Object.entries(itemsObj || {}).filter(([itemId]) => !blockedItemKeys.has(`${date}|${itemId}`))
        );
        if (Object.keys(keptItems).length) next[date] = keptItems;
      });
      return next;
    });

    persistSelectedUser(addressRestrictionModal.updatedUser);
    setAddressRestrictionModal(null);
  };

  const applySelectedAddress = async (address, key, isPrimary = false) => {
    if (isPrimary) {
      const primaryLine = getAddressLineValue(address);
      const primaryCity = getAddressCityValue(address);
      const primaryDivision = getAddressDivisionValue(address);
      const updatedUser = {
        ...user,
        address: primaryLine,
        landmark: address?.Address2?.trim?.() || '',
        area: primaryCity,
        division: primaryDivision || user?.division || '',
        IsCodAvail: address?.IsCodAvail ?? user?.IsCodAvail ?? 0,
        selectedAdditionalAddress: null,
      };

      const restrictionCheck = await validateAddressCartItems(address);
      if (restrictionCheck.affectedItems.length) {
        blockAddressChangeWithCartItems(address, restrictionCheck, updatedUser);
        return;
      }

      persistSelectedUser(updatedUser);
      return;
    }

    const selectedAddressValue = {
      ...address,
      selectedKey: key,
      AddressLine: getAddressLineValue(address),
      City: getAddressCityValue(address),
      Division: getAddressDivisionValue(address),
      isPrimary: false,
    };

    const updatedUser = {
      ...user,
      address: selectedAddressValue.AddressLine,
      landmark: address?.Address2?.trim?.() || '',
      area: selectedAddressValue.City || user?.area || '',
      division: selectedAddressValue.Division || user?.division || '',
      IsCodAvail: address?.IsCodAvail ?? user?.IsCodAvail ?? 0,
      selectedAdditionalAddress: selectedAddressValue,
    };

    const restrictionCheck = await validateAddressCartItems(selectedAddressValue);
    if (restrictionCheck.affectedItems.length) {
      blockAddressChangeWithCartItems(selectedAddressValue, restrictionCheck, updatedUser);
      return;
    }

    persistSelectedUser(updatedUser);
  };

  const resolveAddressId = (address) => {
    const directId = getAddressIdValue(address);
    if (directId) return directId;

    const addressKey = getAddressIdentityKey(address);
    const savedMatch = additionalAddresses.find((item) => getAddressIdentityKey(item) === addressKey && getAddressIdValue(item));
    return getAddressIdValue(savedMatch);
  };

  const handleEditAddress = async (event, address) => {
    event.stopPropagation();
    const addressId = resolveAddressId(address);
    if (!addressId || !user?.Vid) {
      alert('Address ID is missing. Please refresh and try again.');
      return;
    }

    try {
      const res = await api.GetDeleteCustomerAdd(user.Vid, addressId, 'edit');
      const editAddress =
        res?.data?.DeleteCustomerAddjs?.Table?.[0] ||
        res?.DeleteCustomerAddjs?.Table?.[0] ||
        address;

      setIsAddressModalOpen(false);
      navigate('/add-additional-address', {
        state: {
          mode: 'edit',
          address: editAddress,
        },
      });
    } catch (err) {
      console.error('Failed to fetch address for edit:', err);
      alert('Unable to edit this address. Please try again.');
    }
  };

  const handleDeleteAddress = async (event, address) => {
    event.stopPropagation();
    const addressId = resolveAddressId(address);
    if (!addressId || !user?.Vid) {
      alert('Address ID is missing. Please refresh and try again.');
      return;
    }
    if (!window.confirm('Delete this address?')) return;

    try {
      setDeletingAddressId(addressId);
      await api.GetDeleteCustomerAdd(user.Vid, addressId, 'delete');

      setAdditionalAddresses((prev) => prev.filter((item) => getAddressIdValue(item) !== addressId));

      const sessionAddresses = JSON.parse(sessionStorage.getItem('cartAdditionalAddresses') || '[]');
      sessionStorage.setItem(
        'cartAdditionalAddresses',
        JSON.stringify(sessionAddresses.filter((item) => getAddressIdValue(item) !== addressId))
      );

      if (user?.selectedAdditionalAddress && getAddressIdValue(user.selectedAdditionalAddress) === addressId) {
        const updatedUser = { ...user, selectedAdditionalAddress: null };
        setUser(updatedUser);
        sessionStorage.setItem('user', JSON.stringify(updatedUser));
        localStorage.setItem('user', JSON.stringify(updatedUser));
      }
    } catch (err) {
      console.error('Failed to delete address:', err);
      alert('Unable to delete this address. Please try again.');
    } finally {
      setDeletingAddressId(null);
    }
  };

  const renderAddressCard = (address, index, isPrimary = false) => {
    const addressKey = isPrimary ? primaryAddressKey : getAddressKey(address, index);
    const isSelected = isPrimary
      ? isPrimarySelected
      : selectedAddress?.selectedKey === addressKey;
    const addressLine = getAddressLineValue(address);
    const city = getAddressCityValue(address);
    const division = getAddressDivisionValue(address);
    const addressId = getAddressIdValue(address);

    return (
      <div
        key={addressKey}
        onClick={() => applySelectedAddress(address, addressKey, isPrimary)}
        onKeyDown={(event) => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            applySelectedAddress(address, addressKey, isPrimary);
          }
        }}
        role="button"
        tabIndex={0}
        aria-pressed={isSelected}
        className={`w-full p-3 text-left transition-all border rounded-xl ${
          isSelected
            ? 'border-red-400 bg-red-50 shadow-sm ring-2 ring-red-100'
            : 'border-gray-200 bg-white hover:border-red-200 hover:bg-red-50/40'
        }`}
      >
        <div className="flex items-start gap-3">
          <span className={`inline-flex items-center justify-center flex-shrink-0 w-9 h-9 rounded-lg border ${
            isSelected ? 'text-red-600 bg-white border-red-200' : 'text-gray-500 bg-gray-50 border-gray-200'
          }`}>
            {isPrimary ? <HomeIcon size={17} /> : <MapPin size={17} />}
          </span>
          <div className="flex-1 min-w-0">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <h4 className="text-sm font-bold text-gray-900">{address.ShortName || `Address ${index + 1}`}</h4>
              <div className="flex items-center gap-1.5">
                {isSelected && (
                  <span
                    className="inline-flex items-center justify-center w-6 h-6 text-green-600 border border-green-200 rounded-full shadow-sm bg-green-50"
                    aria-label="Selected address"
                    title="Selected address"
                  >
                    <CheckCircle size={15} strokeWidth={2.5} />
                  </span>
                )}
                {!isPrimary && (
                  <>
                    <button
                      type="button"
                      onClick={(event) => handleEditAddress(event, address)}
                      className="inline-flex items-center justify-center text-blue-600 transition-colors border border-blue-100 rounded-full w-7 h-7 bg-blue-50 hover:bg-blue-100"
                      aria-label={`Edit ${address.ShortName || 'address'}`}
                    >
                      <Pencil size={13} />
                    </button>
                    <button
                      type="button"
                      onClick={(event) => handleDeleteAddress(event, address)}
                      disabled={deletingAddressId === addressId}
                      className="inline-flex items-center justify-center text-red-600 transition-colors border border-red-100 rounded-full w-7 h-7 bg-red-50 hover:bg-red-100 disabled:opacity-50"
                      aria-label={`Delete ${address.ShortName || 'address'}`}
                    >
                      <Trash2 size={13} />
                    </button>
                  </>
                )}
              </div>
            </div>
            <p className="mt-1.5 text-sm font-medium leading-relaxed text-gray-800">
              {[addressLine, city].filter(Boolean).join(', ') || 'Address not available'}
            </p>
            {division && (
              <p className="mt-1 text-xs font-medium leading-relaxed text-gray-500">{division}</p>
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <header className="sticky top-0 z-30 w-full bg-white shadow-sm">
      <div className="px-4 mx-auto max-w-7xl sm:px-6 lg:px-8">
        <div className="relative flex items-center justify-center h-16 sm:h-20">
          {/* Left section */}
          <div className="flex items-center">
            <div className="inline-block py-2">
              <Link to="/home">
                <img
                  src={useTamilLogo ? "/assets/AnnamLogoTamil.png" : "/assets/AnnamLogo.PNG"}
                  alt="Logo"
                  className={`${useTamilLogo ? 'w-40 sm:w-48 h-16 sm:h-20' : 'w-28 sm:w-36 h-14 sm:h-16'} object-contain`}
                />
              </Link>
            </div>
          </div>

          {/* Right section - Navigation Tabs */}
          <div className="absolute right-0 items-center hidden space-x-3 -translate-y-1/2 md:flex top-1/2">
            <Link to="/home" className="px-3 py-1.5 rounded-md hover:bg-gray-100 text-gray-700">
              Home
            </Link>
            <Link to="/history" className="px-3 py-1.5 rounded-md hover:bg-gray-100 text-gray-700">
              History
            </Link>

            {/* User Icon with Dropdown */}
            <div className="relative" ref={dropdownRef}>
              <button
                onClick={toggleDropdown}
                className="flex items-center justify-center w-10 h-10 transition-colors bg-red-100 rounded-full hover:bg-red-200 focus:outline-none focus:ring-2 focus:ring-red-500"
                aria-label="User menu"
              >
                {/* User Icon (SVG) */}
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="w-6 h-6 text-red-600"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth={2}
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                  />
                </svg>
              </button>

              {/* Dropdown Menu */}
              {isDropdownOpen && (
                <div className="absolute right-0 z-50 w-56 py-1 mt-2 bg-white border border-gray-200 rounded-lg shadow-lg">
                  {/* User Name */}
                  <div className="px-4 py-3 border-b border-gray-100">
                    <p className="text-sm font-medium text-gray-900">
                      {user?.Vname || user?.name || 'Guest'}
                    </p>
                    <p className="text-xs text-gray-500">{user?.phone || user?.mobile || ''}</p>
                  </div>

                  {/* Help Pages from API */}
                  {/* {helpPages.length > 0 && (
                    <>
                      <div className="px-4 py-2 text-xs font-semibold tracking-wider text-gray-500 uppercase">
                        Information
                      </div>
                      {helpPages.map((page) => (
                        <button
                          key={page.PageId}
                          onClick={() => handleHelpPageClick(page.PageId)}
                          className="w-full text-left px-4 py-2.5 text-sm text-gray-700 hover:bg-red-50 hover:text-red-700 flex items-center"
                        >
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            className="w-5 h-5 mr-3"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                            strokeWidth={2}
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                            />
                          </svg>
                          {page.PageTitle}
                        </button>
                      ))}
                    </>
                  )} */}

                  {/* Divider */}
                  <div className="my-1 border-t border-gray-100"></div>

                  {/* Profile */}
                  <button
                    onClick={handleProfileClick}
                    className="w-full text-left px-4 py-2.5 text-sm text-gray-700 hover:bg-red-50 hover:text-red-700 flex items-center"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="w-5 h-5 mr-3"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                      strokeWidth={2}
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                      />
                    </svg>
                    Profile
                  </button>

                  {/* Contact Annam */}
                  <button
                    onClick={handleContactClick}
                    className="w-full text-left px-4 py-2.5 text-sm text-gray-700 hover:bg-red-50 hover:text-red-700 flex items-center"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="w-5 h-5 mr-3"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                      strokeWidth={2}
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M3 5a2 2 0 012-2h3.28a1 1 0 01.95.68l1.5 4.49a1 1 0 01-.5 1.21l-2.26 1.13a11.04 11.04 0 005.52 5.52l1.13-2.26a1 1 0 011.21-.5l4.49 1.5a1 1 0 01.68.95V19a2 2 0 01-2 2h-1C9.72 21 3 14.28 3 6V5z"
                      />
                    </svg>
                    Contact Annam
                  </button>

                  {/* Terms */}
                  <button
                    onClick={() => handleHelpPageClick(5)}
                    className="w-full text-left px-4 py-2.5 text-sm text-gray-700 hover:bg-red-50 hover:text-red-700 flex items-center"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="w-5 h-5 mr-3"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                      strokeWidth={2}
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                      />
                    </svg>
                    Terms
                  </button>

                  {/* Logout */}
                  <button
                    onClick={handleLogout}
                    className="w-full text-left px-4 py-2.5 text-sm text-red-600 hover:bg-red-50 flex items-center"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="w-5 h-5 mr-3"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                      strokeWidth={2}
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"
                      />
                    </svg>
                    Logout
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      {isAddressModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/55 backdrop-blur-sm">
          <div className="w-full max-w-xl overflow-hidden bg-white shadow-2xl rounded-xl">
            <div className="flex items-center justify-between gap-3 px-4 py-3 text-white bg-red-500">
              <div>
                <p className="text-[11px] font-bold tracking-wide uppercase text-white/80">Delivery Address</p>
                <h3 className="text-base font-bold">Choose Address</h3>
              </div>
              <button
                type="button"
                onClick={() => setIsAddressModalOpen(false)}
                className="inline-flex items-center justify-center w-8 h-8 text-white transition-colors rounded-full hover:bg-white/15"
                aria-label="Close address selector"
              >
                <X size={17} />
              </button>
            </div>

            <div className="p-4 space-y-3 overflow-y-auto max-h-[70vh]">
              <div className="flex justify-end">
                <button
                  type="button"
                  onClick={() => {
                    setIsAddressModalOpen(false);
                    navigate('/add-additional-address');
                  }}
                  className="inline-flex items-center gap-2 px-3 py-2 text-sm font-bold text-red-700 transition-colors border border-red-300 border-dashed rounded-lg bg-red-50 hover:bg-red-100"
                >
                  <Plus size={16} />
                  Add Additional Address
                </button>
              </div>

              {renderAddressCard(primaryAddress, 0, true)}
              {additionalAddresses.map((address, index) => renderAddressCard(address, index))}
            </div>
          </div>
        </div>
      )}
      {addressRestrictionModal && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center p-4 bg-black/55 backdrop-blur-sm">
          <div className="w-full max-w-2xl overflow-hidden bg-white shadow-2xl rounded-2xl">
            <div className="flex items-center justify-between gap-3 px-5 py-3 text-white bg-red-500">
              <div>
                <p className="text-[11px] font-black tracking-wide uppercase text-white/85">Delivery Restriction</p>
                <h3 className="text-lg font-black">Some cart items cannot be delivered</h3>
              </div>
              <button
                type="button"
                onClick={() => setAddressRestrictionModal(null)}
                className="inline-flex items-center justify-center w-8 h-8 text-white transition-colors rounded-full hover:bg-white/15"
                aria-label="Close delivery warning"
              >
                <X size={17} />
              </button>
            </div>
            <div className="p-3 space-y-3">
              <div className="p-3 border border-red-200 rounded-xl bg-red-50">
                <h4 className="text-base font-black text-red-700">
                  {addressRestrictionModal.wording?.header || 'Heavy rain in'} {addressRestrictionModal.addressArea || addressRestrictionModal.addressName}
                </h4>
                <p className="mt-1 text-sm font-medium leading-relaxed text-red-900">
                  {addressRestrictionModal.blockedMealTypes.map(formatMealType).join(', ')} {addressRestrictionModal.wording?.content || 'delivery is temporarily suspended for this address. Your selected address and cart were not changed.'}
                </p>
                <div className="flex flex-wrap gap-2 mt-2">
                  {MEAL_TYPES.map((mealType) => {
                    const isBlocked = addressRestrictionModal.blockedMealTypes.includes(mealType);
                    return (
                      <span
                        key={mealType}
                        className={`px-3 py-1 text-xs font-black rounded-full ${
                          isBlocked ? 'text-red-700 bg-red-100' : 'text-green-700 bg-green-100'
                        }`}
                      >
                        {isBlocked ? '✕' : '✓'} {formatMealType(mealType)} {isBlocked ? 'unavailable' : 'available'}
                      </span>
                    );
                  })}
                </div>
              </div>

              <div className="overflow-hidden border border-gray-200 rounded-xl">
                <div className="px-4 py-2 bg-gray-50">
                  <h4 className="font-black text-gray-800">Affected cart items</h4>
                </div>
                <div className="divide-y divide-gray-100">
                  {addressRestrictionModal.affectedItems.map((item) => (
                    <div key={`${item.date}-${item.itemId}`} className="flex items-center justify-between gap-3 px-4 py-2 bg-white">
                      <div>
                        <p className="text-sm font-black text-gray-900">{item.name}</p>
                        <p className="text-xs font-semibold text-gray-500">{formatMealType(item.mealType).toUpperCase()} - {formatDate(item.date)} - Qty {item.qty}</p>
                      </div>
                      <p className="text-sm font-black text-gray-900">
                        ₹{(Number(item.price || 0) * Number(item.qty || 0)).toFixed(2)}
                      </p>
                    </div>
                  ))}
                </div>
              </div>

              <div className="space-y-2">
                <button
                  type="button"
                  onClick={removeBlockedItemsAndUseAddress}
                  className="w-full px-3 py-2.5 text-sm font-black text-white transition-colors bg-red-500 rounded-xl hover:bg-red-600"
                >
                  Remove {Array.from(new Set(addressRestrictionModal.affectedItems.map((item) => formatMealType(item.mealType)))).join(', ')} Items & Use This Address
                </button>
                <button
                  type="button"
                  onClick={() => setAddressRestrictionModal(null)}
                  className="w-full px-3 py-2.5 text-sm font-black text-gray-700 transition-colors bg-white border border-gray-300 rounded-xl hover:bg-gray-50"
                >
                  Keep Current Address
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setAddressRestrictionModal(null);
                    setIsAddressModalOpen(true);
                  }}
                  className="w-full px-3 py-2.5 text-sm font-black text-red-700 transition-colors border border-red-200 rounded-xl bg-red-50 hover:bg-red-100"
                >
                  Choose Another Address
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </header>
  );
}
